import type { RunnerLevel, RunnerPlayer } from '../../../../domain/runner/runnerTypes';
import { isRunnerShieldBoosterExpiringSoon } from '../../../../domain/runner/runnerPlayer';

/** Continuous pose fields — not published to React every frame. */
export function playerNeedsReactPublish(
  prev: RunnerPlayer,
  next: RunnerPlayer,
): boolean {
  if (prev.lives !== next.lives || prev.maxLives !== next.maxLives) {
    return true;
  }
  const prevFlash = prev.hitInvulnerableRemainingSeconds > 0;
  const nextFlash = next.hitInvulnerableRemainingSeconds > 0;
  if (prevFlash !== nextFlash) {
    return true;
  }
  if (isRunnerShieldBoosterExpiringSoon(prev) !== isRunnerShieldBoosterExpiringSoon(next)) {
    return true;
  }
  const prevBooster = prev.activeBooster;
  const nextBooster = next.activeBooster;
  if (prevBooster == null && nextBooster == null) {
    return false;
  }
  if (prevBooster == null || nextBooster == null) {
    return true;
  }
  return prevBooster.kind !== nextBooster.kind;
}

function countConsumed(
  items: readonly { consumed?: boolean }[],
): number {
  let n = 0;
  for (const item of items) {
    if (item.consumed) {
      n += 1;
    }
  }
  return n;
}

/** Structural / discrete level changes — not path motion of obstacles/decors. */
export function levelNeedsReactPublish(
  prev: RunnerLevel,
  next: RunnerLevel,
): boolean {
  if (prev.modifierZone?.entered !== next.modifierZone?.entered) {
    return true;
  }
  if (countConsumed(prev.sparks) !== countConsumed(next.sparks)) {
    return true;
  }
  if (countConsumed(prev.boosters) !== countConsumed(next.boosters)) {
    return true;
  }
  if (countConsumed(prev.obstacles) !== countConsumed(next.obstacles)) {
    return true;
  }
  // Obstacle opacity drop (e.g. mona after touch) still needs unmount/style via React.
  if (prev.obstacles.length === next.obstacles.length) {
    for (let i = 0; i < prev.obstacles.length; i += 1) {
      const a = prev.obstacles[i];
      const b = next.obstacles[i];
      if (a.id !== b.id) {
        return true;
      }
      if ((a.opacity ?? 1) !== (b.opacity ?? 1)) {
        return true;
      }
      if (Boolean(a.touched) !== Boolean(b.touched)) {
        return true;
      }
    }
  } else {
    return true;
  }
  return false;
}
